import { observeGrantClock } from "./grant-time";
import { z } from "zod";
import { read, transport } from "./api";
export const accessRequestSchema = z.object({
  id: z.string(),
  email: z.string(),
  subject: z.string().optional(),
  server_id: z.string(),
  reason: z.string(),
  duration_minutes: z.number(),
  status: z.string(),
  effective_status: z.string().optional(),
  created_at: z.string(),
  expires_at: z.string().nullish(),
  extends_request_id: z.string().nullish(),
  decision_reason: z.string().nullish(),
});
export const policySchema = z.object({
  server_id: z.string().default(""),
  group_id: z.string().optional(),
  max_duration_minutes: z.number().default(120),
  default_duration_minutes: z.number().default(30),
  approver_emails: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? []),
  approver_group_ids: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? []),
  destinations: z
    .array(z.string())
    .nullish()
    .transform((value) => value ?? []),
  local_forwarding: z.boolean().default(false),
  remote_forwarding: z.boolean().default(false),
  socket_forwarding: z.boolean().default(false),
  shell: z.boolean(),
  exec: z.boolean(),
  sftp: z.boolean(),
  forwarding: z.boolean(),
  jit_required: z.boolean(),
});
export const governanceSchema = z.object({
  requests: z.array(accessRequestSchema).default([]),
  locks: z
    .array(
      z.object({
        kind: z.string(),
        target: z.string(),
        reason: z.string(),
        locked: z.boolean(),
        expires_at: z.string().nullish(),
      }),
    )
    .default([]),
  policies: z.array(policySchema).default([]),
  group_policies: z.array(policySchema).default([]),
  roles: z
    .array(z.object({ email: z.string(), subject: z.string(), roles: z.array(z.string()) }))
    .default([]),
});
export const governanceAPI = {
  resources: () =>
    read(
      "/api/access/resources",
      z.array(
        z.object({
          id: z.string(),
          alias: z.string(),
          login: z.string(),
          jit_required: z.boolean(),
          default_duration_minutes: z.number(),
          max_duration_minutes: z.number(),
        }),
      ),
    ),
  snapshot: () => read("/api/governance", governanceSchema),
  requests: async () => {
    const response = await transport.get("/api/access/requests");
    observeGrantClock(response);
    return z.array(accessRequestSchema).parse(await response.json());
  },
  extend: (id: string, duration_minutes: number, reason: string) =>
    transport.post(`/api/access/requests/${encodeURIComponent(id)}/extend`, {
      json: { duration_minutes, reason },
    }),
  request: (value: { server_id: string; reason: string; duration_minutes: number }) =>
    transport.post("/api/access/requests", { json: value }),
  decide: (id: string, decision: string, reason: string) =>
    transport.post(`/api/governance/requests/${encodeURIComponent(id)}/decision`, {
      json: { decision, reason },
    }),
  revoke: (id: string, reason: string) =>
    transport.post(`/api/governance/requests/${encodeURIComponent(id)}/revoke`, {
      json: { reason },
    }),
  lock: (value: {
    kind: string;
    target: string;
    reason: string;
    locked: boolean;
    expires_at?: string | null;
  }) => transport.post("/api/governance/locks", { json: value }),
  policy: (value: z.infer<typeof policySchema>) =>
    transport.put(`/api/governance/policies/${encodeURIComponent(value.server_id)}`, {
      json: value,
    }),
  groupPolicy: (value: z.infer<typeof policySchema>) =>
    transport.put(`/api/governance/group-policies/${encodeURIComponent(value.group_id ?? "")}`, {
      json: value,
    }),
  roles: (email: string, roles: string[], subject?: string) =>
    transport.put("/api/governance/roles", { json: { email, roles, subject } }),
};
